import { difference, intersection, union } from 'lodash'
import * as Usergroups from '../../../services/cognito/userGroups'
import {
  ALLOW_ADMIN_TOOLS,
  ALLOW_BOOKMARKS_GROUP,
  ALLOW_MY_LEARNING_GROUP,
  ALLOW_PLAYLISTS_GROUP,
  ALLOW_PROFILE_GROUP,
  ALLOW_REPORTING_GROUP,
  ALLOW_SKILL_BITES,
  ALLOW_USER_PAGE_GROUP
} from '../../user-group-policy/permissions'

const ALLOW_ALL = [
  ...Usergroups.ALL_CONSUMER,
  ...Usergroups.ALL_ENTERPRISE,
  Usergroups.ADMIN
]

const ALLOW_ALL_NON_CONSUMER = [...Usergroups.ALL_ENTERPRISE, Usergroups.ADMIN]

const ALLOW_NON_CONSUMER_PLAYLISTS = intersection(
  ALLOW_PLAYLISTS_GROUP,
  ALLOW_ALL_NON_CONSUMER
)

const ALLOW_NON_CONSUMER_BOOKMARKS = intersection(
  ALLOW_BOOKMARKS_GROUP,
  ALLOW_ALL_NON_CONSUMER
)

const ALLOW_NON_CONSUMER_PROFILE = intersection(
  ALLOW_PROFILE_GROUP,
  ALLOW_ALL_NON_CONSUMER
)

const ALLOW_NON_CONSUMER_SKILL_BITES = union(
  ALLOW_SKILL_BITES,
  ALLOW_ALL_NON_CONSUMER
)

export const ALLOW = {
  mobile: {
    menu: {
      accelerate: ALLOW_ALL_NON_CONSUMER,
      mindsetai: ALLOW_ALL,
      howitworks: [Usergroups.BROWSER, Usergroups.FREE],
      blog: Usergroups.ALL_CONSUMER,
      bonusmaterial: [Usergroups.CONSUMER],
      newsletter: Usergroups.BROWSER,
      forbusiness: [Usergroups.BROWSER, Usergroups.FREE, Usergroups.SUSPENDED],
      skillbites: ALLOW_NON_CONSUMER_SKILL_BITES,
      msb: ALLOW_ALL_NON_CONSUMER,
      'ai-skills-practice': ALLOW_ALL_NON_CONSUMER,
      msa: ALLOW_ALL_NON_CONSUMER,
      playlists: ALLOW_PLAYLISTS_GROUP,
      bookmarks: ALLOW_BOOKMARKS_GROUP,
      mylearning: ALLOW_MY_LEARNING_GROUP,
      profile: ALLOW_PROFILE_GROUP,
      'logout-section': Usergroups.ALL_AUTHENTICATED,
      logout: Usergroups.ALL_AUTHENTICATED,
      'admin-tools-label': ALLOW_REPORTING_GROUP,
      reporting: ALLOW_REPORTING_GROUP,
      usermanagement: ALLOW_USER_PAGE_GROUP
    },
    startmenubuttons: {
      explore: ALLOW_ALL
    },
    endmenubuttons: {
      join: [Usergroups.BROWSER, Usergroups.FREE, Usergroups.SUSPENDED],
      signin: [Usergroups.BROWSER]
    }
  },
  tablet: {
    linkbar: {
      more: ALLOW_ALL,
      mindsetai: ALLOW_ALL
    },
    menu: {
      accelerate: ALLOW_ALL_NON_CONSUMER,
      howitworks: [Usergroups.BROWSER, Usergroups.FREE],
      bonusmaterial: [Usergroups.CONSUMER],
      blog: Usergroups.ALL_CONSUMER,
      newsletter: Usergroups.BROWSER,
      forbusiness: [Usergroups.BROWSER, Usergroups.FREE, Usergroups.SUSPENDED],
      skillbites: ALLOW_NON_CONSUMER_SKILL_BITES,
      msb: ALLOW_ALL_NON_CONSUMER,
      'ai-skills-practice': ALLOW_ALL_NON_CONSUMER,
      msa: ALLOW_ALL_NON_CONSUMER,
      playlists: ALLOW_PLAYLISTS_GROUP,
      bookmarks: ALLOW_BOOKMARKS_GROUP,
      mylearning: ALLOW_MY_LEARNING_GROUP,
      profile: ALLOW_PROFILE_GROUP,
      'logout-section': Usergroups.ALL_AUTHENTICATED,
      logout: Usergroups.ALL_AUTHENTICATED,
      'admin-tools-label': ALLOW_REPORTING_GROUP,
      reporting: ALLOW_REPORTING_GROUP,
      usermanagement: ALLOW_USER_PAGE_GROUP
    },
    endmenubuttons: {
      join: [Usergroups.BROWSER, Usergroups.FREE, Usergroups.SUSPENDED],
      signin: [Usergroups.BROWSER]
    }
  },
  smallDesktop: {
    actionbar: {
      divider: [Usergroups.BROWSER, Usergroups.FREE, Usergroups.SUSPENDED]
    },
    linkbar: {
      mindsetai: ALLOW_ALL,
      howitworks: [Usergroups.BROWSER, Usergroups.FREE],
      blog: Usergroups.ALL_AUTHENTICATED_CONSUMER,
      newsletter: Usergroups.BROWSER,
      bonusmaterial: [Usergroups.CONSUMER],
      skillbites: ALLOW_NON_CONSUMER_SKILL_BITES,
      accelerate: ALLOW_ALL_NON_CONSUMER,
      msb: ALLOW_ALL_NON_CONSUMER,
      'ai-skills-practice': ALLOW_ALL_NON_CONSUMER,
      msa: ALLOW_ALL_NON_CONSUMER,
      playlists: [
        Usergroups.CONSUMER,
        Usergroups.MARKETING,
        ...ALLOW_NON_CONSUMER_PLAYLISTS
      ],
      admintools: ALLOW_ADMIN_TOOLS,
      me: ALLOW_PROFILE_GROUP,
      more: [Usergroups.BROWSER, Usergroups.CONSUMER, Usergroups.MARKETING]
    },
    buttonbar: {
      join: [Usergroups.BROWSER, Usergroups.FREE, Usergroups.SUSPENDED],
      signin: [Usergroups.BROWSER],
      more: [Usergroups.FREE, Usergroups.SUSPENDED]
    },
    menu: {
      howitworks: [Usergroups.BROWSER, Usergroups.FREE],
      blog: [Usergroups.BROWSER],
      newsletter: Usergroups.BROWSER,
      forbusiness: [Usergroups.BROWSER, Usergroups.FREE, Usergroups.SUSPENDED],
      bookmarks: Usergroups.ALL_AUTHENTICATED_CONSUMER,
      profile: [
        ...Usergroups.ALL_AUTHENTICATED_CONSUMER,
        ...Usergroups.ALL_ENTERPRISE
      ],
      'logout-section': Usergroups.ALL_AUTHENTICATED,
      logout: Usergroups.ALL_AUTHENTICATED
    },
    me: {
      profile: ALLOW_NON_CONSUMER_PROFILE,
      bookmarks: ALLOW_NON_CONSUMER_BOOKMARKS,
      mylearning: ALLOW_MY_LEARNING_GROUP,
      'logout-section': Usergroups.ALL_AUTHENTICATED,
      logout: Usergroups.ALL_AUTHENTICATED,
      'admin-tools-label': ALLOW_REPORTING_GROUP,
      reporting: ALLOW_REPORTING_GROUP,
      usermanagement: ALLOW_USER_PAGE_GROUP
    },
    adminTools: {
      usermanagement: ALLOW_USER_PAGE_GROUP,
      reporting: ALLOW_REPORTING_GROUP
    }
  },
  largeDesktop: {
    actionbar: {
      divider: [Usergroups.BROWSER, Usergroups.FREE, Usergroups.SUSPENDED]
    },
    linkbar: {
      howitworks: [Usergroups.BROWSER, Usergroups.FREE],
      blog: Usergroups.ALL_CONSUMER,
      bonusmaterial: [Usergroups.CONSUMER],
      newsletter: Usergroups.BROWSER,
      forbusiness: difference(Usergroups.ALL_CONSUMER, [
        Usergroups.CONSUMER,
        Usergroups.MARKETING
      ]),
      skillbites: ALLOW_NON_CONSUMER_SKILL_BITES,
      accelerate: ALLOW_ALL_NON_CONSUMER,
      msb: ALLOW_ALL_NON_CONSUMER,
      'ai-skills-practice': ALLOW_ALL_NON_CONSUMER,
      msa: ALLOW_ALL_NON_CONSUMER,
      playlists: ALLOW_PLAYLISTS_GROUP,
      me: ALLOW_PROFILE_GROUP,
      mindsetai: ALLOW_ALL
    },
    me: {
      profile: difference(
        ALLOW_PROFILE_GROUP,
        Usergroups.ALL_AUTHENTICATED_CONSUMER
      ),
      bookmarks: difference(ALLOW_BOOKMARKS_GROUP, [
        Usergroups.FREE,
        Usergroups.SUSPENDED
      ]),
      mylearning: ALLOW_MY_LEARNING_GROUP,
      'logout-section': Usergroups.ALL_AUTHENTICATED,
      logout: Usergroups.ALL_AUTHENTICATED,
      'admin-tools-label': ALLOW_REPORTING_GROUP,
      reporting: ALLOW_REPORTING_GROUP,
      usermanagement: ALLOW_USER_PAGE_GROUP
    },
    buttonbar: {
      join: [Usergroups.BROWSER, Usergroups.FREE, Usergroups.SUSPENDED],
      signin: [Usergroups.BROWSER],
      account: [Usergroups.CONSUMER, Usergroups.MARKETING],
      more: [Usergroups.FREE, Usergroups.SUSPENDED]
    },
    adminTools: {
      usermanagement: ALLOW_USER_PAGE_GROUP,
      reporting: ALLOW_REPORTING_GROUP
    },
    menu: {
      bookmarks: [Usergroups.FREE, Usergroups.SUSPENDED],
      profile: Usergroups.ALL_AUTHENTICATED_CONSUMER,
      'logout-section': Usergroups.ALL_AUTHENTICATED,
      logout: Usergroups.ALL_AUTHENTICATED
    }
  },
  // extraLargeDesktop is now the same as largeDesktop because the app bar width is restricted.
  extraLargeDesktop: {
    actionbar: {
      divider: [Usergroups.BROWSER, Usergroups.FREE, Usergroups.SUSPENDED]
    },
    linkbar: {
      howitworks: [Usergroups.BROWSER, Usergroups.FREE],
      blog: Usergroups.ALL_CONSUMER,
      bonusmaterial: [Usergroups.CONSUMER],
      newsletter: Usergroups.BROWSER,
      forbusiness: difference(Usergroups.ALL_CONSUMER, [
        Usergroups.CONSUMER,
        Usergroups.MARKETING
      ]),
      skillbites: ALLOW_NON_CONSUMER_SKILL_BITES,
      accelerate: ALLOW_ALL_NON_CONSUMER,
      msb: ALLOW_ALL_NON_CONSUMER,
      'ai-skills-practice': ALLOW_ALL_NON_CONSUMER,
      msa: ALLOW_ALL_NON_CONSUMER,
      mindsetai: ALLOW_ALL,
      playlists: ALLOW_PLAYLISTS_GROUP,
      me: ALLOW_PROFILE_GROUP
    },
    me: {
      profile: difference(
        ALLOW_PROFILE_GROUP,
        Usergroups.ALL_AUTHENTICATED_CONSUMER
      ),
      bookmarks: difference(ALLOW_BOOKMARKS_GROUP, [
        Usergroups.FREE,
        Usergroups.SUSPENDED
      ]),
      mylearning: ALLOW_MY_LEARNING_GROUP,
      'logout-section': Usergroups.ALL_AUTHENTICATED,
      logout: Usergroups.ALL_AUTHENTICATED,
      'admin-tools-label': ALLOW_REPORTING_GROUP,
      reporting: ALLOW_REPORTING_GROUP,
      usermanagement: ALLOW_USER_PAGE_GROUP
    },
    buttonbar: {
      join: [Usergroups.BROWSER, Usergroups.FREE, Usergroups.SUSPENDED],
      signin: [Usergroups.BROWSER],
      account: [Usergroups.CONSUMER, Usergroups.MARKETING],
      more: [Usergroups.FREE, Usergroups.SUSPENDED]
    },
    adminTools: {
      usermanagement: ALLOW_USER_PAGE_GROUP,
      reporting: ALLOW_REPORTING_GROUP
    },
    menu: {
      bookmarks: [Usergroups.FREE, Usergroups.SUSPENDED],
      profile: Usergroups.ALL_AUTHENTICATED_CONSUMER,
      'logout-section': Usergroups.ALL_AUTHENTICATED,
      logout: Usergroups.ALL_AUTHENTICATED
    }
  }
}
